/**
 * Lazy-loaded SEO landing pages. Every page is:
 *   1. A data file in /app/frontend/src/data/seoLandingPages/
 *   2. A one-line entry in the `loaders` map below (dynamic import)
 *   3. Rendered in App.js via <SeoLandingPageBySlug slug={slug} />
 *
 * The heavy template lives in components/seo/SeoLandingPage.jsx.
 *
 * Perf (P-A): each slug's data module is loaded on demand via a dynamic
 * import wrapped in React.lazy, so these ~35 content modules are split out
 * of the eager main bundle and only fetched when their route is visited.
 * Rendered under the app-level <Suspense fallback={<PageLoader/>}> in App.js.
 */
import React from 'react';

// slug -> dynamic import of its data module (each becomes its own chunk)
const loaders = {
  'how-much-does-it-cost-to-build-a-house-adelaide': () => import('../data/seoLandingPages/how-much-cost-build-house-adelaide'),
  'questions-to-ask-a-home-builder-before-signing': () => import('../data/seoLandingPages/questions-to-ask-builder'),
  'my-builder-is-not-responding': () => import('../data/seoLandingPages/builder-not-responding'),
  'when-to-renovate-vs-move': () => import('../data/seoLandingPages/when-to-renovate-vs-move'),
  'how-long-does-a-new-build-take-australia': () => import('../data/seoLandingPages/how-long-new-build-takes'),
  'best-home-builders-adelaide': () => import('../data/seoLandingPages/best-home-builders-adelaide'),
  'should-i-buy-an-established-home-or-build': () => import('../data/seoLandingPages/buy-established-or-build'),
  'my-builder-went-bankrupt': () => import('../data/seoLandingPages/builder-went-bankrupt'),
  'why-is-my-build-so-slow': () => import('../data/seoLandingPages/why-is-my-build-so-slow'),
  'add-granny-flat-south-australia': () => import('../data/seoLandingPages/granny-flat-sa'),
  'should-i-extend-or-sell-my-house': () => import('../data/seoLandingPages/extend-or-sell'),
  'custom-builder-vs-volume-builder': () => import('../data/seoLandingPages/custom-vs-volume-builder'),
  'how-to-shortlist-a-home-builder': () => import('../data/seoLandingPages/how-to-shortlist-builder'),
  'variations-are-killing-my-budget': () => import('../data/seoLandingPages/variations-killing-budget'),
  'red-flags-when-choosing-a-builder': () => import('../data/seoLandingPages/red-flags-choosing-builder'),
  // Track 2 - Life-event pages
  'expecting-a-baby-bigger-house-adelaide': () => import('../data/seoLandingPages/expecting-baby-bigger-house-adelaide'),
  'empty-nester-downsize-adelaide': () => import('../data/seoLandingPages/empty-nester-downsize-adelaide'),
  'multigenerational-home-adelaide': () => import('../data/seoLandingPages/multigenerational-home-adelaide'),
  'building-after-separation-adelaide': () => import('../data/seoLandingPages/building-after-separation-adelaide'),
  'work-from-home-office-renovation-adelaide': () => import('../data/seoLandingPages/work-from-home-office-adelaide'),
  // Track 2 Round 2 - Life-event pages
  'blended-family-home-adelaide': () => import('../data/seoLandingPages/blended-family-home-adelaide'),
  'aging-parents-moving-in-adelaide': () => import('../data/seoLandingPages/aging-parents-moving-in-adelaide'),
  'moving-back-to-adelaide-from-interstate': () => import('../data/seoLandingPages/moving-back-to-adelaide-from-interstate'),
  // Theme 9 SEO expansion - pillars + long-tail
  'dual-occupancy-torrens-title-adelaide': () => import('../data/seoLandingPages/dual-occupancy-torrens-title-adelaide'),
  'best-custom-home-builder-adelaide': () => import('../data/seoLandingPages/best-custom-home-builder-adelaide'),
  'best-fixed-price-builder-adelaide': () => import('../data/seoLandingPages/best-fixed-price-builder-adelaide'),
  'best-first-home-builder-adelaide': () => import('../data/seoLandingPages/best-first-home-builder-adelaide'),
  'best-sloping-block-builder-adelaide': () => import('../data/seoLandingPages/best-sloping-block-builder-adelaide'),
  'best-coastal-home-builder-adelaide': () => import('../data/seoLandingPages/best-coastal-home-builder-adelaide'),
  // VIC life-event pages - Melbourne namespace
  'empty-nester-downsize-melbourne': () => import('../data/seoLandingPages/empty-nester-downsize-melbourne'),
  'expecting-a-baby-bigger-house-melbourne': () => import('../data/seoLandingPages/expecting-baby-bigger-house-melbourne'),
  'multigenerational-home-melbourne': () => import('../data/seoLandingPages/multigenerational-home-melbourne'),
  'building-after-separation-melbourne': () => import('../data/seoLandingPages/building-after-separation-melbourne'),
  'work-from-home-office-renovation-melbourne': () => import('../data/seoLandingPages/work-from-home-office-melbourne'),
  'blended-family-home-melbourne': () => import('../data/seoLandingPages/blended-family-home-melbourne'),
};

// Stable list of slugs for declarative <Route> generation in App.js.
export const SEO_LANDING_SLUGS = Object.keys(loaders);

// One stable React.lazy component per slug (created once, reused across renders
// so navigating to a page does not re-trigger the import).
const lazyComponents = {};
for (const slug of SEO_LANDING_SLUGS) {
  lazyComponents[slug] = React.lazy(() =>
    Promise.all([
      loaders[slug](),
      import('../components/seo/SeoLandingPage'),
    ]).then(([m, tpl]) => {
      const data = m.default || m;
      const Template = tpl.default;
      return { default: () => <Template data={data} /> };
    })
  );
}

/** Lookup wrapper used in App.js route elements. Suspends on first render
 *  (caught by the app-level Suspense) while the data chunk loads. */
export const SeoLandingPageBySlug = ({ slug }) => {
  const LazyPage = lazyComponents[slug];
  if (!LazyPage) return null;
  return <LazyPage />;
};

export default SEO_LANDING_SLUGS;
